import React, {useState} from 'react';
import {Address} from 'types/order';
import {createCard} from 'types/order';
import styles from './CardInformation.module.scss';
import Typography from 'components/Typography';
import Button from 'components/Button';
import Modal from 'components/Modal';
import {Controller, useForm} from 'react-hook-form';
import FormGroup from 'components/FormGroup';
import useUserForm from '../useUserForm';
import toast from 'react-hot-toast';
import TextField from 'components/TextField/TextField';
import SelectField from 'components/SelectField';
import {get} from 'lodash';
import closeIcon from '../../RouteHistory/components/Ticket/close.svg';
import axios from 'axios';
import classNames from 'classnames';
import {format, parseISO} from 'date-fns';
import {CardType, getCardType} from 'helpers/getCardTypeIcon';
import {CreditCardType} from 'components/CreditCardType';

export function cc_format(value: string) {
  var v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  var matches = v.match(/\d{4,16}/g);
  var match = (matches && matches[0]) || '';
  var parts = [];

  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }

  if (parts.length) {
    return parts.join(' ');
  } else {
    return value;
  }
}

export function dd_format(value: string, previousValue: string = '') {
  // Remove all non-numeric characters
  let v = value.replace(/[^0-9]/gi, '');

  // If empty, return empty
  if (!v) return '';

  // Get first digit
  const firstDigit = parseInt(v[0]);

  // If first digit is 2-9, prepend 0 and add separator
  if (v.length === 1 && firstDigit >= 2 && firstDigit <= 9) {
    return '0' + v + '/';
  }

  // If we have at least 2 digits
  if (v.length >= 2) {
    let month = v.substring(0, 2);
    let monthNum = parseInt(month);
    let year = '';

    // If first digit is 0 or 1 but month > 12, reformat
    // e.g., "15" becomes "01/5", "19" becomes "01/9"
    if (monthNum > 12) {
      month = '0' + v[0];
      // Rest of the digits become the year
      year = v.substring(1, 3);
      return month + '/' + year;
    }

    // If we only have 2 digits (month only)
    if (v.length === 2) {
      // Auto-add separator after valid month when typing forward
      if (
        monthNum >= 1 &&
        monthNum <= 12 &&
        previousValue.length < value.length
      ) {
        return month + '/';
      }
      return month;
    }

    // Format as MM/YY (3 or more digits)
    year = v.substring(2, 4);
    return month + '/' + year;
  }

  return v;
}

type AddressEntity = Address & {id: number};
type FormData = Omit<createCard, 'label'>;

type UserAddressesProps = {
  userId?: string;
  addresses: AddressEntity[];
  onChange?: (valud: AddressEntity[]) => void;
  setAddressModalVisible: (val: boolean) => void;
};

const CardInformation: React.FC<UserAddressesProps> = ({
  addresses,
  userId,
  setAddressModalVisible,
}) => {
  const {createNewCard, deleteCard, cards} = useUserForm();

  const [openModal, setOpenModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeCard, setActiveCard] = useState(null);

  const handleOrdersClose = () => setOpenModal(false);

  const {control, setValue, handleSubmit, watch, setError, clearErrors, reset} =
    useForm<FormData>({reValidateMode: 'onChange'});

  const handleCreation = async (data: any) => {
    setLoading(true);

    try {
      const card = {
        card: {
          number: data.card_number.replace(/\s+/g, ''),
          cvc: data.secure_code,
          exp_month: data.expiry_date.split('/')[0],
          exp_year: '20' + data.expiry_date.split('/')[1],
        },
        object: 'token',
        token_type: 'supt',
      };
      const {data: response} = await axios.post(
        'https://api.heartlandportico.com/SecureSubmit.v1/api/token?api_key=pkapi_prod_znAgcDJ1S86y0CPLHq',
        card
      );
      const body = {
        billing_address: data.billing_address.value,
        customer: Number(userId),
        card_type: getCardType(data.card_number),
        card_number: response.card.number,
        token: response.token_value,
        cardholder_name: data.holder_name,
      };
      // console.log('body', data);
      await createNewCard(body);
      setLoading(false);
      setOpenModal(false);
      reset();
    } catch (error) {
      const message =
        get(error, 'response.data.error.message') ||
        'Something wrong with creation. Try again later !';
      toast.error(message);
      setLoading(false);
    }
  };

  const onDelete = (item: any) => {
    setActiveCard(item);
    setDeleteModal(true);
  };

  const deleteCardHandler = async () => {
    setLoading(true);
    try {
      await deleteCard(get(activeCard, 'id'));
      setLoading(false);
      setDeleteModal(false);
    } catch (error) {
      setLoading(false);
    }
  };

  return (
    <div className={styles.accordion}>
      <div>
        <Typography variant="h3" className={styles.accordion__title}>
          Card Information
        </Typography>

        <div className={styles.accordion__inner}>
          {cards.map((card) => (
            <div className={styles.accordion__item} key={get(card, 'id')}>
              <div className={styles.card}>
                <div className={styles.accordion__item_header}>
                  <div>{get(card, 'cardholder_name', '')}</div>
                  <img
                    className={styles.close_icon}
                    alt="close"
                    src={closeIcon}
                    onClick={() => onDelete(card)}
                  />
                </div>
                <div className={styles.row}>
                  <div>**** **** **** {get(card, 'last4', '')}</div>

                  <div className={styles.cardTypeIconWrapper}>
                    {get(card, 'type') && <CreditCardType type={card.type} />}
                  </div>
                </div>
                <div className={styles.row}>
                  <div>
                    Created Date: &nbsp;
                    {format(parseISO(card.created_at), 'MM/dd/yyyy')}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Button
        style={{
          marginRight: 20,
          minHeight: 40,
          marginLeft: 'auto',
          marginTop: 10,
        }}
        color="green"
        onClick={() => setOpenModal(true)}
      >
        Add New Card
      </Button>

      <Modal
        isOpen={openModal}
        onClose={handleOrdersClose}
        className={styles.list_wrapper}
      >
        <div className={styles.modalWrapper}>
          <div className={styles.cardTypeWrapper}>
            <div
              className={classNames(
                styles.cardType,
                getCardType(watch('card_number') || '') &&
                  styles.cardTypeVisible
              )}
            >
              {getCardType(watch('card_number') || '') && (
                <CreditCardType
                  type={getCardType(watch('card_number')) as CardType}
                />
              )}
            </div>
            <FormGroup>
              <Controller
                name="card_number"
                control={control}
                rules={{
                  onChange: (e) => {
                    const value = cc_format(e.target.value);
                    if (value && value.length === 18 && !getCardType(value)) {
                      setError('card_number', {
                        message: 'Invalid card number',
                      });
                    } else {
                      clearErrors('card_number');
                    }
                  },
                  required: 'Please, fill card number',
                  minLength: {
                    value: 18,
                    message: 'Please, fill card number',
                  },
                }}
                render={({field, fieldState}) => (
                  <TextField
                    label="Card Number"
                    onChange={(event) =>
                      field.onChange(cc_format(event.target.value))
                    }
                    onBlur={field.onBlur}
                    wrapperClass={styles.googleInput}
                    placeholder="**** **** **** 1234"
                    // hasError={false}
                    value={field.value}
                    error={fieldState.error?.message}
                  />
                )}
              />
            </FormGroup>
          </div>

          <FormGroup columns={2} className={styles.modalWrapper}>
            <Controller
              name="expiry_date"
              control={control}
              rules={{
                required: 'Please, fill expiry date',
                minLength: {
                  value: 5,
                  message: 'Please, fill expiry date',
                },
                validate: (value) => {
                  if (!value) return 'Please, fill expiry date';
                  const parts = value.split('/');
                  if (parts.length !== 2) return 'Invalid expiry date format';

                  const month = parseInt(parts[0]);
                  const year = parseInt(parts[1]);

                  if (month < 1 || month > 12) return 'Invalid month';

                  // Check if card is expired
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear() % 100; // Get last 2 digits
                  const currentMonth = currentDate.getMonth() + 1;

                  if (
                    year < currentYear ||
                    (year === currentYear && month < currentMonth)
                  ) {
                    return 'Card is expired';
                  }

                  return true;
                },
              }}
              render={({field, fieldState}) => (
                <TextField
                  label="Expiry Date"
                  wrapperClass={styles.googleInput}
                  placeholder="mm/yy"
                  maxLength={5}
                  hasError={false}
                  onChange={(event) => {
                    const formatted = dd_format(
                      event.target.value,
                      field.value || ''
                    );
                    field.onChange(formatted);
                  }}
                  onBlur={field.onBlur}
                  value={field.value}
                  error={fieldState.error?.message}
                />
              )}
            />
            <Controller
              name="secure_code"
              control={control}
              rules={{
                required: 'Please, fill secure code',
                minLength: {
                  value: 3,
                  message: 'Please, fill secure code',
                },
              }}
              render={({field, fieldState}) => (
                <TextField
                  maxLength={4}
                  label="Secure Code"
                  value={field.value}
                  wrapperClass={styles.googleInput}
                  placeholder="CVC"
                  hasError={false}
                  onChange={(event) => {
                    setValue(
                      'secure_code',
                      event.target.value.replace(/\D/g, '')
                    );
                  }}
                  error={fieldState.error?.message}
                />
              )}
            />
          </FormGroup>
          <FormGroup className={styles.switchForm}>
            <Controller
              name="holder_name"
              control={control}
              rules={{required: 'Please, fill holder name'}}
              render={({field, fieldState}) => (
                <TextField
                  label="Card Holder Name"
                  wrapperClass={styles.googleInput}
                  placeholder="Enter card holder name"
                  hasError={false}
                  onChange={field.onChange}
                  error={fieldState.error?.message}
                />
              )}
            />
          </FormGroup>
          <FormGroup style={{position: 'relative', marginTop: 10}}>
            <Controller
              name="billing_address"
              control={control}
              rules={{required: 'Please select address'}}
              render={({field: {ref, ...field}, fieldState}) => (
                <SelectField
                  {...field}
                  label="Billing Address"
                  placeholder="Select address"
                  options={addresses
                    .filter((item) => item.type === 'billing')
                    .map((item) => ({
                      value: item.id,
                      label: `${item.address_line_1}, ${item.city}, ${item.state}, ${item.zipcode}`,
                    }))}
                  error={fieldState.error?.message}
                  value={field.value}
                  onChange={(newValue) => {
                    if (newValue) {
                      setValue('billing_address', newValue);
                    }
                  }}
                />
              )}
            />
            <Button
              style={{
                position: 'absolute',
                padding: 0,
                height: 18,
                width: 18,
                borderRadius: 10,
                paddingBottom: 1,
                left: 110,
                top: 21,
                zIndex: 1,
              }}
              onClick={() => {
                setAddressModalVisible(true);
              }}
              component="a"
              color="green"
            >
              +
            </Button>
          </FormGroup>
          <div className={styles.actionWrapper}>
            <Button
              loading={loading}
              type="submit"
              onClick={handleSubmit(handleCreation)}
              color="green"
            >
              Save
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={deleteModal}
        onClose={() => setDeleteModal(false)}
        onRequestClose={() => setDeleteModal(false)}
        className={styles.delete_wrapper}
      >
        <p className={styles.modalHeader}>
          Are you sure, you want to delete the card ?
        </p>
        <div className={styles.accordion}>
          <div className={styles.accordion__inner}>
            <div className={styles.accordion__item}>
              <div className={styles.card}>
                <div className={styles.accordion__item_header}>
                  <div>{get(activeCard, 'cardholder_name', '')}</div>
                </div>
                <div className={styles.row}>
                  <div>**** **** **** {get(activeCard, 'last4', '')}</div>
                  <div>
                    Expiration: {get(activeCard, 'exp_month', '')}/
                    {get(activeCard, 'exp_year', '')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Button
          className={styles.delete}
          onClick={deleteCardHandler}
          color="danger"
          size="md"
          loading={loading}
        >
          Delete card
        </Button>
      </Modal>
    </div>
  );
};

export default CardInformation;
